import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { cn } from "../../lib/utils";
import { crmApi } from "./api";
import { ConfirmModal } from "../../components/ui/confirm-modal";
import { useToast } from "../../components/ui/toast";
import { RoleModal } from "./role-modal";
import { CRM_PERMISSION_LABELS, type CrmRole, type CrmRolePermissions } from "./types";

export function CrmRolesPage() {
  const cache = useQueryClient();
  const toast = useToast();
  const team = useQuery({ queryKey: ["crm-team"], queryFn: crmApi.team });
  const isAdmin = team.data?.role === "admin";
  const roles = useQuery({ queryKey: ["crm-roles"], queryFn: crmApi.roles, enabled: isAdmin });
  const [editing, setEditing] = useState<CrmRole | "new" | null>(null);
  const [pendingDelete, setPendingDelete] = useState<CrmRole | null>(null);
  const [error, setError] = useState("");

  const refresh = async () => {
    await Promise.all([
      cache.invalidateQueries({ queryKey: ["crm-roles"] }),
      cache.invalidateQueries({ queryKey: ["crm-team"] }),
    ]);
  };

  const save = async (data: { name: string; permissions: CrmRolePermissions }) => {
    if (editing === "new" || !editing) await crmApi.createRole(data.name, data.permissions);
    else await crmApi.updateRole(editing.id, data);
    await refresh();
  };

  const toggleActive = async (role: CrmRole) => {
    try { await crmApi.updateRole(role.id, { active: !role.active }); await refresh(); }
    catch (e) { setError(e instanceof Error ? e.message : "Unable to update role."); }
  };

  const holdersOf = (roleId: string) => (team.data?.members ?? []).filter(m => m.role === roleId);

  const remove = async () => {
    if (!pendingDelete) return;
    const target = pendingDelete;
    // Holders are moved to Unassigned first, so the role is always freeable.
    const holders = holdersOf(target.id);
    try {
      for (const holder of holders) {
        await crmApi.editAgent(holder.id, { role: "", active: holder.active });
      }
      await crmApi.deleteRole(target.id);
      await refresh();
      toast.success(
        `Deleted "${target.name}"`,
        holders.length > 0 ? `${holders.map(h => h.name).join(", ")} moved to Unassigned.` : undefined,
      );
      setPendingDelete(null);
    } catch (e) {
      toast.danger("Couldn't delete role", e instanceof Error ? e.message : "Unable to delete role.");
      setPendingDelete(null);
    }
  };

  return (
    <div className="p-6 lg:p-8">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-base font-semibold text-ink">Roles</h1>
          <p className="mt-2 text-sm text-ink-3">{roles.data?.length ?? 0} roles — define exactly what each one can do with leads.</p>
        </div>
        {isAdmin && (
          <button onClick={() => setEditing("new")} className="flex items-center gap-2 self-start rounded-xl bg-brand px-4 py-3 font-semibold text-white">
            <Plus className="h-4 w-4" /> New role
          </button>
        )}
      </div>

      {!isAdmin && team.data ? (
        <p className="mt-5">Role management is available to business admins.</p>
      ) : (
        <>
      {(error || roles.error) && <p role="alert" className="mt-4 text-red-600">{error || (roles.error as Error).message}</p>}
      {roles.isLoading && <p className="mt-4">Loading roles…</p>}
      {roles.data?.length === 0 && (
        <p className="mt-4 text-sm text-ink-3">No roles yet — create one with New role, then assign users to it.</p>
      )}

      <div className="mt-6 grid gap-4 md:grid-cols-2">
        {roles.data?.map(role => (
          <div key={role.id} className="rounded-xl border border-line bg-elevated p-5">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="flex items-center gap-2">
                  <h2 className="font-semibold text-ink">{role.name}</h2>
                </div>
                <p className="mt-1 font-mono text-[11px] text-ink-3">{role.id}</p>
              </div>
              <span className={cn("rounded-full px-2.5 py-1 text-xs font-semibold", role.active ? "bg-green-100 text-green-700" : "bg-soft text-ink-3")}>
                {role.active ? "Active" : "Inactive"}
              </span>
            </div>

            <div className="mt-4 flex flex-wrap gap-1.5">
              {(Object.keys(CRM_PERMISSION_LABELS) as (keyof CrmRolePermissions)[]).filter(key => role.permissions[key]).map(key => (
                <span key={key} className="rounded-full bg-brand-soft px-2.5 py-1 text-[11px] font-semibold text-brand">{CRM_PERMISSION_LABELS[key]}</span>
              ))}
              {(Object.keys(CRM_PERMISSION_LABELS) as (keyof CrmRolePermissions)[]).every(key => !role.permissions[key]) && (
                <span className="text-xs text-ink-3">No permissions</span>
              )}
            </div>

            <div className="mt-4 flex items-center gap-2 border-t border-line pt-4">
              <button onClick={() => setEditing(role)} className="flex items-center gap-1.5 rounded-lg bg-soft px-3 py-2 text-xs font-semibold text-ink-2 hover:bg-line">
                <Pencil className="h-3.5 w-3.5" /> Edit
              </button>
              <button onClick={() => void toggleActive(role)} className="rounded-lg bg-soft px-3 py-2 text-xs font-semibold text-ink-2 hover:bg-line">
                {role.active ? "Deactivate" : "Activate"}
              </button>
              <button
                onClick={() => setPendingDelete(role)}
                className="ml-auto flex items-center gap-1.5 rounded-lg px-3 py-2 text-xs font-semibold text-ink-3 hover:bg-red-500/10 hover:text-red-600"
              >
                <Trash2 className="h-3.5 w-3.5" /> Delete
              </button>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <RoleModal role={editing === "new" ? null : editing} onClose={() => setEditing(null)} onSave={save} />
      )}

      {pendingDelete && (() => {
        const holders = holdersOf(pendingDelete.id);
        return (
          <ConfirmModal
            title="Delete role"
            body={holders.length > 0
              ? `Delete "${pendingDelete.name}"? ${holders.map(h => h.name).join(", ")} will be moved to Unassigned.`
              : `Delete "${pendingDelete.name}"?`}
            confirmLabel="Delete"
            onConfirm={() => void remove()}
            onClose={() => setPendingDelete(null)}
          />
        );
      })()}
        </>
      )}
    </div>
  );
}
